import clientInfo from 'common/lib/clientInfo';

import copyMagicLog from '../../shared/copyMagicLog';
import { generateSessionId } from '../../shared/dataHelpers';

const SESSION_ID_REGEX =
  /^\d{10}-[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// This is used to speed up session id validation.
const SESSION_IDS_CACHE = new Set<string>();

type SetNewChatSessionIdInput = {
  restarted?: boolean;
};

const getStorageKey = () =>
  'kjb_copyMagic_conversationAssistant'.concat(
    '_userId',
    clientInfo.get('user_id'),
    '_chatSessionId',
  );

const isValid = (sessionId: string | null): boolean => {
  if (!sessionId) return false;
  if (SESSION_IDS_CACHE.has(sessionId)) return true;
  if (!sessionId.match(SESSION_ID_REGEX)) return false;

  SESSION_IDS_CACHE.add(sessionId);
  return true;
};

const getChatSessionId = (): string => {
  const sessionId = localStorage.getItem(getStorageKey());
  if (isValid(sessionId)) return String(sessionId);

  return setNewChatSessionId();
};

const getChatSessionIdWithStatus = (): { chatSessionId: string; isNew: boolean } => {
  const sessionId = localStorage.getItem(getStorageKey());
  if (isValid(sessionId)) {
    return {
      chatSessionId: String(sessionId),
      isNew: false,
    };
  }

  return {
    chatSessionId: setNewChatSessionId(),
    isNew: true,
  };
};

const setNewChatSessionId = ({ restarted = false }: SetNewChatSessionIdInput = {}): string => {
  const sessionId = generateSessionId();
  const storageKey = getStorageKey();
  localStorage.setItem(storageKey, sessionId);
  SESSION_IDS_CACHE.add(sessionId);

  copyMagicLog({
    message: `useChatSessionId: session ${restarted ? 'restarted' : 'started'}`,
    key: storageKey,
    sessionId,
  });

  return sessionId;
};

// This hook is used to track chat session id across multiple tabs in the same browser.
const useChatSessionId = () => ({
  getChatSessionId,
  setNewChatSessionId,
  getChatSessionIdWithStatus,
});

export default useChatSessionId;
